/* eslint-disable complexity */
import { ErrorBanner } from '@core/components';
import { LabelWithScoreThresholdSelector, type ScoreLabelOption, type LabelWithScore } from '@core/components/LabelWithScoreThresholdSelector';
import { LabelWithSeveritySelector, type LabelOption, type LabelWithSeverity } from '@core/components/LabelWithSeveritySelector';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { TextInput, LoadingSpinner, Switch, Dialog } from '@soteria/design-system-ve';
import { ContentType } from '@system-config/label-management/domain';
import { useStandardLabels } from '@system-config/label-management/hooks';
import { PolicyScope, type StandardPolicy, type PolicyLabel } from '@system-config/policy-management/domain';
import { useCreateStandardPolicy, useUpdateStandardPolicy } from '@system-config/policy-management/hooks';
import { useEffect, useState, useMemo, useCallback } from 'react';

import * as styles from './StandardPolicyDialog.css.ts';

interface StandardPolicyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  policy?: StandardPolicy;
}

/**
 * Sanitizes labels by trimming labelId and removing Apollo's __typename
 */
function sanitizePolicyLabel(label: PolicyLabel): PolicyLabel {
  const { __typename, ...cleanLabel } = label as PolicyLabel & { __typename?: string };

  return {
    ...cleanLabel,
    labelId: cleanLabel.labelId.trim(),
  };
}

/**
 * Checks if a label is a text label (has severity)
 */
function isTextLabel(label: PolicyLabel): boolean {
  return typeof label.severity === 'number' && (label.scoreThreshold === undefined || label.scoreThreshold === null);
}

/**
 * Checks if a label is an image label (has scoreThreshold)
 */
function isImageLabel(label: PolicyLabel): boolean {
  return typeof label.scoreThreshold === 'number' && (label.severity === undefined || label.severity === null);
}

interface FormData {
  name: string;
  description: string;
  isEnabled: boolean;
  labels: PolicyLabel[];
}

function StandardPolicyDialogComponent({ isOpen, onClose, policy }: StandardPolicyDialogProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    isEnabled: true,
    labels: [],
  });

  const { labels: availableLabels, loading: labelsLoading } = useStandardLabels();

  // Separate text and image labels
  const textLabelOptions: LabelOption[] = useMemo(
    () =>
      availableLabels
        .filter(label => label.contentType === ContentType.Text)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
          maxSeverity: label.maxSeverity ?? undefined,
        })),
    [availableLabels]
  );

  const imageLabelOptions: ScoreLabelOption[] = useMemo(
    () =>
      availableLabels
        .filter(label => label.contentType === ContentType.Image)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
        })),
    [availableLabels]
  );

  // Transform PolicyLabel[] to LabelWithSeverity[] (text labels only)
  const textLabelsWithSeverity: LabelWithSeverity[] = useMemo(
    () =>
      (formData.labels || [])
        .filter(label => typeof label.severity === 'number' && label.severity >= 1)
        .map(label => ({
          labelId: label.labelId,
          severity: label.severity ?? 0,
        })),
    [formData.labels]
  );

  // Transform PolicyLabel[] to LabelWithScore[] (image labels only)
  const imageLabelsWithScore: LabelWithScore[] = useMemo(
    () =>
      (formData.labels || [])
        .filter(label => typeof label.scoreThreshold === 'number' && label.scoreThreshold >= 0)
        .map(label => ({
          labelId: label.labelId,
          scoreThreshold: label.scoreThreshold ?? 0,
        })),
    [formData.labels]
  );

  // Handle text label changes
  const handleTextLabelsChange = useCallback(
    (labels: LabelWithSeverity[]) => {
      const textPolicyLabels: PolicyLabel[] = labels.map(label => ({
        labelId: label.labelId,
        severity: label.severity,
      }));

      // Keep existing image labels
      const imagePolicyLabels = formData.labels.filter(isImageLabel);

      setFormData(prev => ({
        ...prev,
        labels: [...textPolicyLabels, ...imagePolicyLabels],
      }));
    },
    [formData.labels]
  );

  // Handle image label changes
  const handleImageLabelsChange = useCallback(
    (labels: LabelWithScore[]) => {
      const imagePolicyLabels: PolicyLabel[] = labels.map(label => ({
        labelId: label.labelId,
        scoreThreshold: label.scoreThreshold,
      }));

      // Keep existing text labels
      const textPolicyLabels = formData.labels.filter(isTextLabel);

      setFormData(prev => ({
        ...prev,
        labels: [...textPolicyLabels, ...imagePolicyLabels],
      }));
    },
    [formData.labels]
  );

  useEffect(() => {
    // Reset form data when dialog opens or policy changes
    if (isOpen) {
      setFormData({
        name: policy?.name || '',
        description: policy?.description || '',
        isEnabled: policy?.isEnabled ?? true,
        labels: policy?.labels || [],
      });
    }
  }, [isOpen, policy]);

  const { createStandardPolicy, loading: isCreating, errors: createErrors, resetErrors: resetCreateErrors } = useCreateStandardPolicy();
  const { updateStandardPolicy, loading: isUpdating, errors: updateErrors, resetErrors: resetUpdateErrors } = useUpdateStandardPolicy();

  const isLoading = isCreating || isUpdating;
  const errors = createErrors || updateErrors;

  // Helper function to validate policy labels
  function isValidPolicyLabel(label: PolicyLabel | null | undefined): label is PolicyLabel {
    if (!label || typeof label.labelId !== 'string' || label.labelId.trim() === '') {
      return false;
    }

    // Text label: must have severity >= 1
    const hasValidSeverity = typeof label.severity === 'number' && label.severity >= 1;

    // Image label: must have scoreThreshold >= 0
    const hasValidScoreThreshold = typeof label.scoreThreshold === 'number' && label.scoreThreshold >= 0;

    // Ensure mutual exclusivity between severity and scoreThreshold
    return (hasValidSeverity && !hasValidScoreThreshold) || (hasValidScoreThreshold && !hasValidSeverity);
  }

  const isFormValid = () => {
    // Check required fields - name is required, labels are optional for standard policies
    const requiredFieldsValid = formData.name.trim() !== '';

    // If editing, also check if anything changed
    if (policy) {
      const hasChanges =
        formData.name !== policy.name ||
        formData.description !== (policy.description || '') ||
        formData.isEnabled !== policy.isEnabled ||
        JSON.stringify(formData.labels) !== JSON.stringify(policy.labels);

      return requiredFieldsValid && hasChanges;
    }

    return requiredFieldsValid;
  };

  const handleInputChange = (field: keyof FormData) => (value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    resetCreateErrors();
    resetUpdateErrors();

    // Filter and sanitize labels
    const validLabels = formData.labels.filter(isValidPolicyLabel).map(sanitizePolicyLabel);

    if (policy) {
      // Update existing policy - add scope for API compatibility
      const updateData = {
        ...formData,
        labels: validLabels,
        scope: PolicyScope.Global, // Always Global for standard policies
      };
      const success = await updateStandardPolicy(policy.id, updateData);

      if (success) {
        onClose();
      }
    } else {
      // Create new policy - add scope for API compatibility
      const createData = {
        ...formData,
        labels: validLabels,
        scope: PolicyScope.Global, // Always Global for standard policies
      };
      const success = await createStandardPolicy(createData);

      if (success) {
        onClose();
      }
    }
  };

  return (
    <Dialog
      cancelAction={{ label: 'Close' }}
      description={policy ? `Edit the ${policy.name} standard policy` : 'Create a new standard policy'}
      isOpen={isOpen}
      primaryAction={{
        label: isLoading ? <LoadingSpinner /> : policy ? 'Save Changes' : 'Add Policy',
        onClick: handleSubmit,
        disabled: isLoading || !isFormValid() || labelsLoading,
      }}
      size="lg"
      title={policy ? `Edit ${policy.name}` : 'Add Standard Policy'}
      onClose={() => {
        resetCreateErrors();
        resetUpdateErrors();
        onClose();
      }}
    >
      <div className={styles.form}>
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the policy:" />}

        {/* Policy Status Toggle - Only show when editing */}
        {policy && (
          <div className={styles.toggleContainer}>
            <span className={styles.toggleLabel}>Enabled</span>
            <Switch checked={formData.isEnabled} id="isEnabled" label="Toggle policy enabled status" onChange={handleInputChange('isEnabled')} />
          </div>
        )}

        {/* Policy Name Input */}
        <TextInput required id="name" label="Policy Name" placeholder="Enter policy name" value={formData.name} onChange={handleInputChange('name')} />

        {/* Policy Description Input */}
        <TextInput
          id="description"
          label="Description"
          placeholder="Enter policy description"
          value={formData.description}
          onChange={handleInputChange('description')}
        />

        {/* Text Labels Selector */}
        {textLabelOptions.length > 0 && (
          <LabelWithSeveritySelector
            showSeverityDescription
            availableLabels={textLabelOptions}
            defaultMaxSeverity={3}
            defaultSeverity={2}
            emptyStateMessage="No text labels selected. Add labels to define text content criteria."
            loading={labelsLoading}
            selectedLabels={textLabelsWithSeverity}
            title="Text Content Labels"
            onChange={handleTextLabelsChange}
          />
        )}

        {/* Image Labels Selector */}
        {imageLabelOptions.length > 0 && (
          <LabelWithScoreThresholdSelector
            showScoreDescription
            availableLabels={imageLabelOptions}
            defaultScoreThreshold={70}
            emptyStateMessage="No image labels selected. Add labels to define image content criteria."
            loading={labelsLoading}
            maxScore={100}
            minScore={0}
            selectedLabels={imageLabelsWithScore}
            title="Image Content Labels"
            onChange={handleImageLabelsChange}
          />
        )}
      </div>
    </Dialog>
  );
}

export const StandardPolicyDialog = withAppInsightsTracking(StandardPolicyDialogComponent, 'StandardPolicyDialog');
